import { Button, Card, Skeleton, Space, Table, Typography } from "antd"
import { useState } from "react"
import { projectTypeName } from "../../../common/constants"
import { centsToDollarsDisplay } from "../../displayFormatters"
import ProposalTimeline from "../../jobs/Proposals/ProposalTimeline"
import { Proposal } from "../../jobs/Proposals/proposalsModel"
import { getProjectUrl } from "../datacenterModel"
import { ProposalReportData, ProposalsSummary } from "./proposalsReportApi"
import { useGetJobProposalsQuery } from "./proposalsReportQueries"
import ExportCsvButton from "../ExportCsvButton"
import useDashboardReporting from "../../../hooks/useDashboardReporting"
import { CsvDateRange } from "../reportCsvExport"
import { WithStatus } from "../statusBucketsExport"
import { proposalExportColumns } from "./proposalsReportExport"

const { Text } = Typography

type FiltersListItem = {
  id: string
  value: string
}

const filtersList: FiltersListItem[] = [
  { id: 'unsent', value: 'Drafts' },
  { id: 'sent', value: 'Sent' },
  { id: 'pending', value: 'Pending' },
  { id: 'approved', value: 'Approved' },
  { id: 'lost', value: 'Lost' }
]

type Props = {
  proposals: ProposalsSummary
  exportRows: WithStatus<ProposalReportData>[]
  dateRange: CsvDateRange
}

const ProposalsReportJobList = ({ proposals, exportRows, dateRange }: Props) => {
  const dashboardReporting = useDashboardReporting()
  const [activeFilter, setActiveFilter] = useState<string>('unsent');
  const [expandedJobId, setExpandedJobId] = useState<number[]>([])
  const proposalsQuery = useGetJobProposalsQuery(expandedJobId.length > 0 ? expandedJobId[0] : undefined)
  const proposalsEvents = [...(proposalsQuery.data ?? [])].reverse() as Proposal[]

  const filteredProposals = proposals[activeFilter]

  return <Card
    extra={dashboardReporting && <ExportCsvButton reportName='Proposal Summary' dateRange={dateRange} columns={proposalExportColumns} rows={exportRows} tooltip='Exports every status, with a Status column' />}
    title={<Space>
        {
          filtersList.map((filter) => <Button
              key={filter.id}
              shape='round'
              type={activeFilter === filter.id ? 'primary' : 'default'}
              onClick={() => setActiveFilter(filter.id)}
            >
              {filter.value}
            </Button>
          )
        }
    </Space>}
  >
    <Table 
      columns={[
        { title: 'Name', dataIndex: 'jobName', key: 'jobName' },
        { title: 'Customer', dataIndex: 'displayName', key: 'displayName' },
        { title: 'Property', dataIndex: 'property', key: 'property', render: (_, record: ProposalReportData) => <Text>{`${record.address1}, ${record.city}, ${record.state}, ${record.zip}`}</Text> },
        { title: 'Final Price', dataIndex: 'price', key: 'price', render: (value: number) => <Text>{`${centsToDollarsDisplay(value)}`}</Text> },
        { title: 'Job Type', dataIndex: 'jobTypeName', key: 'jobTypeName', render: (_, record: ProposalReportData) => <Text>{projectTypeName(record.jobType)}</Text> },
        { title: 'Sales Person', dataIndex: 'salesPersonName', key: 'salesPersonName' },
        { title: 'Division', dataIndex: 'divisions', key: 'divisions', render: (_, record: ProposalReportData) => 
          <Text>{record.divisions ? [...new Set(record.divisions.split(';').map(d => d.trim()))].join(', ') : ''}</Text> }
      ]}
      dataSource={filteredProposals}
      rowKey={({ projectId }: ProposalReportData) => projectId}
      onRow={(record: ProposalReportData) => {
        const jobUrl = getProjectUrl(record.projectId, record.jobType, record.isSold, record.jobName)

        return {
          onClick: () => window.open(`#!/${jobUrl}`, '_blank'),
          style: { cursor: 'pointer' }
        }}
      }
      expandable={{
        expandedRowRender: () => proposalsQuery.isLoading 
          ? <Skeleton active />
          : <div style={{ paddingLeft: 50 }}>
              {
                proposalsEvents?.map((proposal, index) => <div key={proposal.id}>
                  <Text strong>Version {proposalsEvents.length - index}</Text>
                  <ProposalTimeline key={proposal.id} latestProposal={proposal} />
                </div>)
              }
            </div>,
        onExpand: (_, { projectId }) => {
          if (expandedJobId.includes(projectId)) {
            setExpandedJobId([])
            return
          }

          setExpandedJobId([projectId])
        },
        expandedRowKeys: expandedJobId
      }}
    />
  </Card>
}

export default ProposalsReportJobList